import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Chip,
  IconButton,
  InputAdornment,
  Alert,
  Tooltip,
  Autocomplete,
  Divider
} from '@mui/material';
import {
  Add,
  Edit,
  Search as SearchIcon,
  Clear as ClearIcon,
  Schedule,
  AttachFile,
  Archive,
  Delete,
  Restore,
  Undo,
  Place,
  Block,
  SwapHoriz,
  DragIndicator,
  ViewColumn
} from '@mui/icons-material';
import TableSortLabel from '@mui/material/TableSortLabel';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ROLES } from '../../constants/roles';
import { PART_UNIT_CONDITIONS, PART_CONDITION_LABELS, partConditionSelectValue } from '../../constants/partUnitConditions';
import { useToast } from '../../contexts/ToastContext';
import StoreDocumentsDialog from './StoreDocumentsDialog';
import InventoryItemDetailModal from './InventoryItemDetailModal';
import InventoryItemFormDialog from './InventoryItemFormDialog';
import { formatPartMasterOptionLabel, getInventoryDisplayExpiryDate } from '../../utils/partMasterLabel';
import { STORE_TYPES, normalizeStoreTypeValue, formatStoreTypeLabel } from '../../constants/storeTypes';
import { UNIT_METRIC_OPTIONS, unitMetricLabel } from '../../constants/unitMetrics';

/** Default left-to-right order for the inventory table (Actions stays fixed on the right). */
const DEFAULT_INVENTORY_COLUMN_ORDER = [
  'partNumber',
  'referencePartNumber',
  'purchaseOrderNumber',
  'aircraft',
  'description',
  'category',
  'storeType',
  'locationText',
  'quantity',
  'unitMetric',
  'location',
  'status',
  'condition',
  'expiryDate'
];

const INVENTORY_COLUMN_META = {
  partNumber: { label: 'Part Number', align: 'left' },
  referencePartNumber: { label: 'Reference Part Number', align: 'left' },
  purchaseOrderNumber: { label: 'PO Number', align: 'left' },
  aircraft: { label: 'Aircraft', align: 'left' },
  description: { label: 'Description', align: 'left' },
  category: { label: 'Category', align: 'left' },
  storeType: { label: 'Store Type', align: 'left' },
  locationText: { label: 'Loc. note', align: 'left' },
  quantity: { label: 'Quantity', align: 'right' },
  unitMetric: { label: 'Unit Metric', align: 'left' },
  location: { label: 'Location', align: 'left' },
  status: { label: 'Status', align: 'left' },
  condition: { label: 'Condition', align: 'left' },
  expiryDate: { label: 'Expiry date', align: 'left' }
};

const DEFAULT_INVENTORY_SORT = { orderBy: 'partNumber', order: 'asc' };

function inventoryTablePrefsStorageKey(domain, userId) {
  const scope = domain || 'STORE';
  return userId
    ? `inventoryTablePrefs:${scope}:${userId}`
    : `inventoryTablePrefs:${scope}`;
}

/** Legacy key used before per-user prefs (still read for migration). */
function inventoryColumnOrderStorageKey(domain) {
  return `inventoryTableColumnOrder:${domain || 'STORE'}`;
}

function normalizeInventoryColumnOrder(saved) {
  const known = new Set(DEFAULT_INVENTORY_COLUMN_ORDER);
  const fromSaved = Array.isArray(saved)
    ? saved.filter((id) => known.has(id))
    : [];
  const missing = DEFAULT_INVENTORY_COLUMN_ORDER.filter((id) => !fromSaved.includes(id));
  return [...fromSaved, ...missing];
}

function normalizeInventorySort(saved) {
  const orderBy = typeof saved?.orderBy === 'string' && saved.orderBy
    ? saved.orderBy
    : DEFAULT_INVENTORY_SORT.orderBy;
  const order = saved?.order === 'desc' ? 'desc' : 'asc';
  return { orderBy, order };
}

function loadInventoryTablePrefs(domain, userId) {
  const defaults = {
    columnOrder: [...DEFAULT_INVENTORY_COLUMN_ORDER],
    ...DEFAULT_INVENTORY_SORT
  };
  try {
    const prefsKey = inventoryTablePrefsStorageKey(domain, userId);
    const rawPrefs = localStorage.getItem(prefsKey);
    if (rawPrefs) {
      const parsed = JSON.parse(rawPrefs);
      return {
        columnOrder: normalizeInventoryColumnOrder(parsed?.columnOrder),
        ...normalizeInventorySort(parsed)
      };
    }

    // Migrate legacy column-order-only key (shared or per-browser).
    const legacyRaw = localStorage.getItem(inventoryColumnOrderStorageKey(domain));
    if (legacyRaw) {
      const columnOrder = normalizeInventoryColumnOrder(JSON.parse(legacyRaw));
      const migrated = { columnOrder, ...DEFAULT_INVENTORY_SORT };
      saveInventoryTablePrefs(domain, userId, migrated);
      return migrated;
    }

    return defaults;
  } catch {
    return defaults;
  }
}

function saveInventoryTablePrefs(domain, userId, prefs) {
  try {
    const payload = {
      columnOrder: normalizeInventoryColumnOrder(prefs?.columnOrder),
      ...normalizeInventorySort(prefs)
    };
    localStorage.setItem(
      inventoryTablePrefsStorageKey(domain, userId),
      JSON.stringify(payload)
    );
    // Keep legacy key in sync so older builds still see column order.
    localStorage.setItem(
      inventoryColumnOrderStorageKey(domain),
      JSON.stringify(payload.columnOrder)
    );
    return true;
  } catch {
    return false;
  }
}

const EXPORT_COLUMNS = [
  'aircraftRegistration',
  'partNumber',
  'referencePartNumber',
  'description',
  'category',
  'quantity',
  'unitMetric',
  'status',
  'warehouse',
  'shelf',
  'bin',
  'expiryDate'
];

const toCsvCell = (value) => {
  const stringValue = value === null || value === undefined ? '' : String(value);
  return `"${stringValue.replace(/"/g, '""')}"`;
};

function isInventoryToolCalibrationDueSoon(item) {
  if (!item || item.category !== 'TOOL' || !item.expiryDate) return false;
  const d = new Date(item.expiryDate);
  const w = new Date();
  w.setDate(w.getDate() + 30);
  return d <= w;
}

function isInventoryToolCalibrationOverdue(item) {
  if (!item || item.category !== 'TOOL' || !item.expiryDate) return false;
  return new Date(item.expiryDate) < new Date();
}

/** Shelf life (parts/materials/consumables) expiring within 30 days. Tools use calibration helpers. */
function isInventoryShelfLifeExpiringSoon(item) {
  if (!item || item.category === 'TOOL') return false;
  const expiry = getInventoryDisplayExpiryDate(item);
  if (!expiry) return false;
  const w = new Date();
  w.setDate(w.getDate() + 30);
  return new Date(expiry) <= w;
}

/** Shelf life already past for parts/materials/consumables. */
function isInventoryShelfLifeExpired(item) {
  if (!item || item.category === 'TOOL') return false;
  const expiry = getInventoryDisplayExpiryDate(item);
  if (!expiry) return false;
  return new Date(expiry) < new Date();
}

function getLocationString(item) {
  const loc = item?.location;
  if (!loc) return '';
  return [loc.warehouse, loc.shelf, loc.bin].filter(Boolean).join(' / ');
}

/** Display string for aircraft column (matches table body). */
function getAircraftDisplaySortKey(item) {
  if (item?.aircraftScope === 'AIRCRAFT') {
    return String(item.aircraftId?.registration || item.aircraftRegistration || '').toLowerCase();
  }
  return 'gen';
}

const INVENTORY_NUMERIC_SORT_FIELDS = new Set([
  'quantity',
  'unitCost',
  'shelfLife',
  'expiryDate',
  'createdAt',
  'updatedAt',
  'archivedAt',
  'isArchived',
  'isLowStock'
]);

/** All inventory row sort keys (table headers + extra fields for toolbar). */
const INVENTORY_SORT_OPTIONS = [
  { id: 'partNumber', label: 'Part number' },
  { id: 'referencePartNumber', label: 'Reference part number' },
  { id: 'purchaseOrderNumber', label: 'PO number' },
  { id: 'aircraft', label: 'Aircraft (display)' },
  { id: 'aircraftRegistration', label: 'Aircraft registration (raw)' },
  { id: 'aircraftScope', label: 'Aircraft scope' },
  { id: 'description', label: 'Description' },
  { id: 'category', label: 'Category' },
  { id: 'storeType', label: 'Store type' },
  { id: 'locationText', label: 'Location (free text)' },
  { id: 'quantity', label: 'Quantity' },
  { id: 'unitMetric', label: 'Unit metric' },
  { id: 'location', label: 'Location (warehouse / shelf / bin)' },
  { id: 'warehouse', label: 'Warehouse' },
  { id: 'shelf', label: 'Shelf' },
  { id: 'bin', label: 'Bin' },
  { id: 'status', label: 'Status' },
  { id: 'condition', label: 'Condition' },
  { id: 'expiryDate', label: 'Expiry / calibration due' },
  { id: 'createdAt', label: 'Created' },
  { id: 'updatedAt', label: 'Updated' },
  { id: 'archivedAt', label: 'Archived at' },
  { id: 'isArchived', label: 'Archived' },
  { id: 'isLowStock', label: 'Low stock flag' },
  { id: 'unitCost', label: 'Unit cost' },
  { id: 'shelfLife', label: 'Shelf life' },
  { id: 'batchNumber', label: 'Batch number' },
  { id: 'serialNumber', label: 'Serial number' },
  { id: 'partCatalogRef', label: 'Parts catalog link' },
  { id: 'lastLinkRunId', label: 'Last part-link run' },
  { id: 'certificateNumber', label: 'Certificate number' },
  { id: 'airworthinessTag', label: 'Airworthiness tag' }
];

function getInventorySortComparable(item, key) {
  switch (key) {
    case 'partNumber':
      return (item.partNumber || '').toLowerCase();
    case 'referencePartNumber':
      return (item.referencePartNumber || '').toLowerCase();
    case 'purchaseOrderNumber':
      return (item.purchaseOrderNumber || '').toLowerCase();
    case 'aircraft':
      return getAircraftDisplaySortKey(item);
    case 'aircraftRegistration':
      return (item.aircraftRegistration || '').toLowerCase();
    case 'aircraftScope':
      return (item.aircraftScope || '').toLowerCase();
    case 'description':
      return (item.description || '').toLowerCase();
    case 'category':
      return (item.category || '').toLowerCase();
    case 'storeType':
      return (item.storeType || '').toLowerCase();
    case 'locationText':
      return (item.locationText || '').toLowerCase();
    case 'quantity':
      return Number.isFinite(Number(item.quantity)) ? Number(item.quantity) : 0;
    case 'unitMetric':
      return (item.unitMetric || '').toLowerCase();
    case 'location':
      return getLocationString(item).toLowerCase();
    case 'warehouse':
      return (item.location?.warehouse || '').toLowerCase();
    case 'shelf':
      return (item.location?.shelf || '').toLowerCase();
    case 'bin':
      return (item.location?.bin || '').toLowerCase();
    case 'status':
      return (item.status || '').toLowerCase();
    case 'condition': {
      const raw = item.condition || '';
      return (PART_CONDITION_LABELS[raw] || raw || '').toLowerCase();
    }
    case 'expiryDate':
      return item.expiryDate ? new Date(item.expiryDate).getTime() : null;
    case 'createdAt':
      return item.createdAt ? new Date(item.createdAt).getTime() : null;
    case 'updatedAt':
      return item.updatedAt ? new Date(item.updatedAt).getTime() : null;
    case 'archivedAt':
      return item.archivedAt ? new Date(item.archivedAt).getTime() : null;
    case 'isArchived':
      return item.isArchived ? 1 : 0;
    case 'isLowStock':
      return item.isLowStock ? 1 : 0;
    case 'unitCost':
      return Number.isFinite(Number(item.unitCost)) ? Number(item.unitCost) : 0;
    case 'shelfLife':
      return Number.isFinite(Number(item.shelfLife)) ? Number(item.shelfLife) : 0;
    case 'batchNumber':
      return (item.batchNumber || '').toLowerCase();
    case 'serialNumber':
      return (item.serialNumber || '').toLowerCase();
    case 'partCatalogRef': {
      const ref = item.partCatalogRef;
      const id = ref && typeof ref === 'object' ? ref._id : ref;
      return String(id || '').toLowerCase();
    }
    case 'lastLinkRunId':
      return (item.lastLinkRunId || '').toLowerCase();
    case 'certificateNumber':
      return (item.certificateNumber || '').toLowerCase();
    case 'airworthinessTag':
      return (item.airworthinessTag || '').toLowerCase();
    default:
      return (item[key] != null ? String(item[key]) : '').toLowerCase();
  }
}

function compareInventorySortValues(va, vb, field) {
  const numeric = INVENTORY_NUMERIC_SORT_FIELDS.has(field);
  if (numeric) {
    const aNull = va == null || Number.isNaN(va);
    const bNull = vb == null || Number.isNaN(vb);
    if (aNull && bNull) return 0;
    if (aNull) return 1;
    if (bNull) return -1;
    const diff = Number(va) - Number(vb);
    return diff;
  }
  return String(va ?? '').localeCompare(String(vb ?? ''), undefined, { sensitivity: 'base' });
}

const InventoryManagement = ({ inventoryDomain = 'STORE' }) => {
  const storeApi = useCallback(
    (path) => `/api/store${inventoryDomain === 'LOAN' ? '/loan' : ''}${path}`,
    [inventoryDomain]
  );
  const LINK_RUN_ID_STORAGE_KEY = 'inventoryPartLinkLastRunId';
  const [inventory, setInventory] = useState([]);
  const [filteredInventory, setFilteredInventory] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [detailItemId, setDetailItemId] = useState(null);
  const [selectedToolPanel, setSelectedToolPanel] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [categoryFilter, setCategoryFilter] = useState('ALL');
  const [locationFilter, setLocationFilter] = useState('ALL');
  const [storeTypeFilter, setStoreTypeFilter] = useState('ALL');
  const [aircraftFilter, setAircraftFilter] = useState([]);
  const [conditionFilter, setConditionFilter] = useState('ALL');
  const OPERATOR_STORAGE_KEY = `inventorySelectedOperator_${inventoryDomain}`;
  const [operators, setOperators] = useState([]);
  const [selectedOperatorId, setSelectedOperatorId] = useState(() => localStorage.getItem(OPERATOR_STORAGE_KEY) || '');
  const [aircraftList, setAircraftList] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [orderBy, setOrderBy] = useState('partNumber');
  const [order, setOrder] = useState('asc');
  const [archiveDialog, setArchiveDialog] = useState({ open: false, item: null, action: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, item: null, dependencies: null });
  const [quarantineDialog, setQuarantineDialog] = useState({ open: false, item: null });
  const [quarantining, setQuarantining] = useState(false);
  const { user } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [documentsDialog, setDocumentsDialog] = useState({
    open: false,
    entityId: null,
    label: ''
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [revertingLink, setRevertingLink] = useState(false);
  const [lastLinkRunId, setLastLinkRunId] = useState(() => localStorage.getItem(LINK_RUN_ID_STORAGE_KEY) || '');
  const [columnOrder, setColumnOrder] = useState(
    () => loadInventoryTablePrefs(inventoryDomain, null).columnOrder
  );
  const [columnReorderMode, setColumnReorderMode] = useState(false);
  const [dragColumnId, setDragColumnId] = useState(null);
  const [dropTargetColumnId, setDropTargetColumnId] = useState(null);
  const dragColumnIdRef = useRef(null);

  const canEdit = [ROLES.STORE_INSPECTOR, ROLES.STORE_EXECUTIVE, ROLES.ACCOUNTABLE_MANAGER, ROLES.AM_MRO, ROLES.SUPER_ADMIN].includes(user?.role);
  const prefsUserId = user?._id ? String(user._id) : null;

  const persistTablePrefs = useCallback((next) => {
    return saveInventoryTablePrefs(inventoryDomain, prefsUserId, next);
  }, [inventoryDomain, prefsUserId]);

  useEffect(() => {
    const prefs = loadInventoryTablePrefs(inventoryDomain, prefsUserId);
    setColumnOrder(prefs.columnOrder);
    setOrderBy(prefs.orderBy);
    setOrder(prefs.order);
  }, [inventoryDomain, prefsUserId]);

  const reorderColumns = useCallback((fromId, toId) => {
    if (!fromId || !toId || fromId === toId) return;
    const fromIndex = columnOrder.indexOf(fromId);
    const toIndex = columnOrder.indexOf(toId);
    if (fromIndex < 0 || toIndex < 0) return;
    const next = [...columnOrder];
    next.splice(fromIndex, 1);
    next.splice(toIndex, 0, fromId);
    setColumnOrder(next);
    const ok = persistTablePrefs({ columnOrder: next, orderBy, order });
    showToast(
      ok ? 'Column order saved' : 'Could not save column order in this browser',
      ok ? 'success' : 'warning'
    );
  }, [columnOrder, persistTablePrefs, orderBy, order, showToast]);

  const handleColumnDragStart = useCallback((event, columnId) => {
    if (!columnReorderMode) {
      event.preventDefault();
      return;
    }
    dragColumnIdRef.current = columnId;
    setDragColumnId(columnId);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', columnId);
  }, [columnReorderMode]);

  const handleColumnDragOver = useCallback((event, columnId) => {
    if (!columnReorderMode) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    if (columnId !== dropTargetColumnId) setDropTargetColumnId(columnId);
  }, [columnReorderMode, dropTargetColumnId]);

  const handleColumnDrop = useCallback((event, columnId) => {
    event.preventDefault();
    if (!columnReorderMode) return;
    const fromId = event.dataTransfer.getData('text/plain') || dragColumnIdRef.current || dragColumnId;
    reorderColumns(fromId, columnId);
    dragColumnIdRef.current = null;
    setDragColumnId(null);
    setDropTargetColumnId(null);
  }, [columnReorderMode, dragColumnId, reorderColumns]);

  const handleColumnDragEnd = useCallback(() => {
    dragColumnIdRef.current = null;
    setDragColumnId(null);
    setDropTargetColumnId(null);
  }, []);

  const handleStartColumnReorder = useCallback(() => {
    setColumnReorderMode(true);
    showToast('Drag column grips to reorder, then click Done', 'info');
  }, [showToast]);

  const handleDoneColumnReorder = useCallback(() => {
    setColumnReorderMode(false);
    dragColumnIdRef.current = null;
    setDragColumnId(null);
    setDropTargetColumnId(null);
  }, []);

  const handleResetColumnOrder = useCallback(() => {
    const reset = [...DEFAULT_INVENTORY_COLUMN_ORDER];
    setColumnOrder(reset);
    const ok = persistTablePrefs({ columnOrder: reset, orderBy, order });
    showToast(ok ? 'Column order reset' : 'Could not save column order in this browser', ok ? 'success' : 'warning');
  }, [persistTablePrefs, orderBy, order, showToast]);

  const isCustomColumnOrder = useMemo(
    () => columnOrder.join(',') !== DEFAULT_INVENTORY_COLUMN_ORDER.join(','),
    [columnOrder]
  );

  const getColumnHeaderLabel = useCallback((columnId) => {
    if (columnId === 'expiryDate') {
      return categoryFilter === 'TOOL' ? 'Calibration due' : 'Expiry date';
    }
    return INVENTORY_COLUMN_META[columnId]?.label || columnId;
  }, [categoryFilter]);

  const renderInventoryColumnCell = useCallback((columnId, item) => {
    switch (columnId) {
      case 'partNumber':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {item.category === 'TOOL' && isInventoryToolCalibrationDueSoon(item) && (
              <Schedule
                color={isInventoryToolCalibrationOverdue(item) ? 'error' : 'warning'}
                fontSize="small"
                titleAccess={isInventoryToolCalibrationOverdue(item) ? 'Calibration overdue' : 'Calibration due within 30 days'}
              />
            )}
            {isInventoryShelfLifeExpiringSoon(item) && (
              <Schedule
                color={isInventoryShelfLifeExpired(item) ? 'error' : 'warning'}
                fontSize="small"
                titleAccess={isInventoryShelfLifeExpired(item) ? 'Shelf life expired' : 'Shelf life expiring within 30 days'}
              />
            )}
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
              {item.partNumber}
            </Typography>
          </Box>
        );
      case 'referencePartNumber':
        return (
          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
            {item.referencePartNumber || '—'}
          </Typography>
        );
      case 'purchaseOrderNumber':
        return (
          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
            {item.purchaseOrderNumber || '—'}
          </Typography>
        );
      case 'aircraft':
        return item.aircraftScope === 'AIRCRAFT'
          ? item.aircraftId?.registration || item.aircraftRegistration || '—'
          : 'GEN';
      case 'description':
        return item.description;
      case 'category':
        return <Chip label={item.category} size="small" />;
      case 'storeType':
        return formatStoreTypeLabel(item.storeType);
      case 'locationText':
        return item.locationText || '—';
      case 'quantity':
        return (
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {Number.isFinite(Number(item.quantity)) ? Number(item.quantity) : 0}
          </Typography>
        );
      case 'unitMetric':
        return (
          <Typography variant="body2">
            {unitMetricLabel(item.unitMetric)}
          </Typography>
        );
      case 'location':
        return item.location?.warehouse ? (
          <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
            {[item.location.warehouse, item.location.shelf, item.location.bin]
              .filter(Boolean)
              .join(' / ')}
          </Typography>
        ) : null;
      case 'status':
        return (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Chip
              label={item.status}
              size="small"
              color={
                item.status === 'IN_INVENTORY'
                  ? 'success'
                  : item.status === 'QUARANTINED'
                  ? 'error'
                  : 'default'
              }
            />
            {item.isArchived && (
              <Chip
                label="Archived"
                size="small"
                color="default"
                variant="outlined"
              />
            )}
          </Box>
        );
      case 'condition':
        return PART_CONDITION_LABELS[item.condition] || item.condition || '—';
      case 'expiryDate': {
        const expiryDate = getInventoryDisplayExpiryDate(item);
        if (item.category !== 'TOOL' && item.shelfLifeNotApplicable && !expiryDate) {
          return <Typography variant="caption" color="text.secondary">N/A</Typography>;
        }
        if (!expiryDate) {
          return item.category === 'TOOL'
            ? <Typography variant="caption" color="text.secondary">No date</Typography>
            : null;
        }
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
            <Typography
              variant="body2"
              color={
                item.category === 'TOOL' && isInventoryToolCalibrationOverdue(item)
                  ? 'error'
                  : 'text.secondary'
              }
            >
              {new Date(expiryDate).toLocaleDateString()}
            </Typography>
            {item.category === 'TOOL' && isInventoryToolCalibrationDueSoon(item) && (
              <Chip
                label={isInventoryToolCalibrationOverdue(item) ? 'Cal. overdue' : 'Cal. ≤30d'}
                size="small"
                color={isInventoryToolCalibrationOverdue(item) ? 'error' : 'warning'}
              />
            )}
            {isInventoryShelfLifeExpiringSoon(item) && (
              <Chip
                label={isInventoryShelfLifeExpired(item) ? 'Expired' : 'Expires ≤30d'}
                size="small"
                color={isInventoryShelfLifeExpired(item) ? 'error' : 'warning'}
              />
            )}
          </Box>
        );
      }
      default:
        return null;
    }
  }, []);

  // Reset to first page when filter results change (e.g. fewer items)
  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(filteredInventory.length / rowsPerPage) - 1);
    if (page > maxPage) setPage(0);
  }, [filteredInventory.length, rowsPerPage, page]);

  const fetchInventory = useCallback(async (includeArchivedFlag = null) => {
    try {
      const flag = includeArchivedFlag !== null ? includeArchivedFlag : showArchived;
      const response = await axios.get(storeApi('/inventory'), {
        params: {
          includeArchived: flag,
          ...(conditionFilter !== 'ALL' ? { condition: conditionFilter } : {})
        }
      });
      setInventory(response.data);
    } catch (error) {
      console.error('Error fetching inventory:', error);
      showToast('Error loading inventory', 'error');
    }
  }, [showToast, showArchived, storeApi, conditionFilter]);

  useEffect(() => {
    fetchInventory();
  }, [showArchived, conditionFilter, fetchInventory]);

  useEffect(() => {
    const loadAircraft = async () => {
      try {
        const res = await axios.get('/api/aircraft');
        const list = Array.isArray(res.data) ? res.data : res.data?.aircraft || res.data?.data || [];
        setAircraftList(Array.isArray(list) ? list : []);
      } catch (e) {
        console.error('Error fetching aircraft for inventory filters', e);
      }
    };
    loadAircraft();
  }, []);

  useEffect(() => {
    axios.get('/api/master-data/operators').then((res) => {
      const active = (res.data || []).filter((op) => op.isActive !== false);
      setOperators(active);
      const stored = localStorage.getItem(OPERATOR_STORAGE_KEY) || '';
      const valid = active.some((op) => op._id === stored);
      if (valid) {
        setSelectedOperatorId(stored);
      } else if (active.length === 1) {
        setSelectedOperatorId(active[0]._id);
        localStorage.setItem(OPERATOR_STORAGE_KEY, active[0]._id);
      } else {
        setSelectedOperatorId('');
        localStorage.removeItem(OPERATOR_STORAGE_KEY);
      }
    }).catch(() => {});
  }, [OPERATOR_STORAGE_KEY]);

  const uniqueLocations = useMemo(() => {
    const locs = inventory.map((i) => getLocationString(i)).filter(Boolean);
    return [...new Set(locs)].sort();
  }, [inventory]);



  const uniqueAircraft = useMemo(() => {
    return [...aircraftList].sort((a, b) => String(a.registration || '').localeCompare(String(b.registration || '')));

  }, [aircraftList, inventory]);

  const aircraftFilterOptions = useMemo(() => {
    const opts = [{ id: 'GEN', label: 'GEN' }];
    uniqueAircraft.forEach((aircraft) => {
      const modelPart = aircraft.model || aircraft.modelVariant
        ? ` (${aircraft.model || aircraft.modelVariant})`
        : '';
      opts.push({ id: String(aircraft._id), label: `${aircraft.registration || aircraft._id}${modelPart}` });
    });
    return opts;
  }, [uniqueAircraft]);

  const matchesAircraftFilter = useCallback(
    (item, selectedAircraftIds) => {
      if (!Array.isArray(selectedAircraftIds) || selectedAircraftIds.length === 0) return true;
      return selectedAircraftIds.some((id) => {
        if (id === 'GEN') return (item.aircraftScope || 'GEN') === 'GEN';
        return String(item.aircraftId?._id || item.aircraftId || '') === String(id);
      });
    },
    []
  );

  const filterInventory = useCallback(() => {
    let filtered = [...inventory];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(item =>
        (item.partNumber || '').toLowerCase().includes(term) ||
        (item.referencePartNumber || '').toLowerCase().includes(term) ||
        (item.description || '').toLowerCase().includes(term)
      );
    }

    if (statusFilter !== 'ALL') {
      filtered = filtered.filter(item => item.status === statusFilter);
    }

    if (categoryFilter !== 'ALL') {
      filtered = filtered.filter(item => item.category === categoryFilter);
    }

    if (locationFilter !== 'ALL') {
      filtered = filtered.filter(item => getLocationString(item) === locationFilter);
    }

    if (storeTypeFilter !== 'ALL') {
      filtered = filtered.filter(
        (item) => normalizeStoreTypeValue(item.storeType) === storeTypeFilter
      );
    }

    if (aircraftFilter.length > 0) {
      filtered = filtered.filter((item) => matchesAircraftFilter(item, aircraftFilter));
    }

    filtered.sort((a, b) => {
      const aVal = getInventorySortComparable(a, orderBy);
      const bVal = getInventorySortComparable(b, orderBy);
      const cmp = compareInventorySortValues(aVal, bVal, orderBy);
      return order === 'asc' ? cmp : -cmp;
    });

    setFilteredInventory(filtered);
  }, [inventory, searchTerm, statusFilter, categoryFilter, locationFilter, storeTypeFilter, aircraftFilter, orderBy, order, matchesAircraftFilter]);

  useEffect(() => {
    filterInventory();
  }, [filterInventory]);

  useEffect(() => {
    if (categoryFilter !== 'TOOL') setSelectedToolPanel(null);
  }, [categoryFilter]);

  const handleOpen = (item = null) => {
    setEditingItem(item || null);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingItem(null);
  };

  const handleDocumentsDialogOpen = (item) => {
    setDocumentsDialog({
      open: true,
      entityId: item._id,
      label: formatPartMasterOptionLabel(item)
    });
  };

  const handleDocumentsDialogClose = () => {
    setDocumentsDialog({ open: false, entityId: null, label: '' });
  };

  const handleInventorySaved = (savedItem, errorMessage, merged) => {
    if (errorMessage) {
      showToast(errorMessage, 'error');
      return;
    }
    if (!savedItem) return;
    showToast(
      editingItem
        ? 'Inventory item updated'
        : (merged ? 'Inventory already existed; quantity was added' : 'Inventory item created'),
      'success'
    );
    if (savedItem._id) {
      setDocumentsDialog({
        open: true,
        entityId: savedItem._id,
        label: formatPartMasterOptionLabel(savedItem)
      });
    }
    fetchInventory();
    handleClose();
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('ALL');
    setCategoryFilter('ALL');
    setLocationFilter('ALL');
    setStoreTypeFilter('ALL');
    setAircraftFilter([]);
    setConditionFilter('ALL');
  };

  const handleSortRequest = (columnId) => {
    const isAsc = orderBy === columnId && order === 'asc';
    const nextOrder = isAsc ? 'desc' : 'asc';
    setOrderBy(columnId);
    setOrder(nextOrder);
    setPage(0);
    persistTablePrefs({ columnOrder, orderBy: columnId, order: nextOrder });
  };

  const hasActiveFilters = () =>
    searchTerm !== '' || statusFilter !== 'ALL' || categoryFilter !== 'ALL' || locationFilter !== 'ALL' || storeTypeFilter !== 'ALL' || aircraftFilter.length > 0 || conditionFilter !== 'ALL';

  const handleArchive = async () => {
    try {
      await axios.put(storeApi(`/inventory/${archiveDialog.item._id}/archive`));
      showToast('Item archived successfully', 'success');
      setArchiveDialog({ open: false, item: null, action: null });
      fetchInventory();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Error archiving item';
      const dependencies = error.response?.data?.dependencies;
      
      if (dependencies) {
        setDeleteDialog({
          open: true,
          item: archiveDialog.item,
          dependencies,
          isArchive: true
        });
        setArchiveDialog({ open: false, item: null, action: null });
      } else {
        showToast(errorMessage, 'error');
      }
    }
  };

  const handleQuarantine = async () => {
    const item = quarantineDialog.item;
    if (!item) return;
    try {
      setQuarantining(true);
      const expiry = getInventoryDisplayExpiryDate(item);
      const expiryLabel = expiry ? new Date(expiry).toLocaleDateString() : 'unknown';
      await axios.put(storeApi(`/inventory/${item._id}`), {
        storeType: 'QUARANTINE',
        status: 'QUARANTINED',
        statusNotes: `Moved to quarantine store: shelf life expiring/expired (${expiryLabel})`
      });
      showToast('Item moved to quarantine store', 'success');
      setQuarantineDialog({ open: false, item: null });
      fetchInventory();
    } catch (error) {
      showToast(error.response?.data?.message || 'Error moving item to quarantine', 'error');
    } finally {
      setQuarantining(false);
    }
  };

  const handleRestore = async () => {
    try {
      await axios.put(storeApi(`/inventory/${archiveDialog.item._id}/restore`));
      showToast('Item restored successfully', 'success');
      setArchiveDialog({ open: false, item: null, action: null });
      fetchInventory();
    } catch (error) {
      showToast(error.response?.data?.message || 'Error restoring item', 'error');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(storeApi(`/inventory/${deleteDialog.item._id}`));
      showToast('Item permanently deleted', 'success');
      setDeleteDialog({ open: false, item: null, dependencies: null });
      fetchInventory();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Error deleting item';
      const dependencies = error.response?.data?.dependencies;
      
      if (dependencies) {
        setDeleteDialog({
          open: true,
          item: deleteDialog.item,
          dependencies,
          isArchive: false
        });
      } else {
        showToast(errorMessage, 'error');
        setDeleteDialog({ open: false, item: null, dependencies: null });
      }
    }
  };

  const handleExportCsv = () => {
    const rowsToExport = filteredInventory || [];
    if (rowsToExport.length === 0) {
      showToast('No inventory rows available to export', 'warning');
      return;
    }

    const lines = [
      EXPORT_COLUMNS.map(toCsvCell).join(','),
      ...rowsToExport.map((item) => {
        const row = {
          aircraftRegistration: item.aircraftRegistration || 'GEN',
          partNumber: item.partNumber || '',
          referencePartNumber: item.referencePartNumber || '',
          description: item.description || '',
          category: item.category || '',
          quantity: Number.isFinite(Number(item.quantity)) ? Number(item.quantity) : 0,
          unitMetric: item.unitMetric || '',
          status: item.status || '',
          warehouse: item.location?.warehouse || '',
          shelf: item.location?.shelf || '',
          bin: item.location?.bin || '',
          expiryDate: item.expiryDate ? new Date(item.expiryDate).toISOString().slice(0, 10) : ''
        };
        return EXPORT_COLUMNS.map((column) => toCsvCell(row[column])).join(',');
      })
    ];

    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `inventory-export-${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    showToast(`Exported ${rowsToExport.length} inventory rows`, 'success');
  };

  const handleRevertPartLinkRun = async () => {
    if (!lastLinkRunId) {
      showToast('No link run available to revert', 'warning');
      return;
    }
    try {
      setRevertingLink(true);
      const response = await axios.post(storeApi('/inventory/link-parts/revert'), { runId: lastLinkRunId });
      const data = response.data || {};
      showToast(`Reverted ${data.reverted || 0} items for run ${lastLinkRunId}`, 'success');
      setLastLinkRunId('');
      localStorage.removeItem(LINK_RUN_ID_STORAGE_KEY);
      fetchInventory();
    } catch (error) {
      console.error('Error reverting parts catalog link run:', error);
      showToast(error.response?.data?.message || 'Failed to revert part-link run', 'error');
    } finally {
      setRevertingLink(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">Inventory Management</Typography>
        {canEdit && (
          <Button variant="contained" startIcon={<Add />} onClick={() => handleOpen()}>
            Add Item
          </Button>
        )}
      </Box>

      {/* Search and Filters */}
      <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={8} md={3}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by Part Number, Reference Part Number, or Description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setSearchTerm('')} size="small">
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="ALL">All Status</MenuItem>
                <MenuItem value="IN_INVENTORY">In Inventory</MenuItem>
                <MenuItem value="ISSUED">Issued</MenuItem>
                <MenuItem value="QUARANTINED">Quarantined</MenuItem>
                <MenuItem value="RETURNED">Returned</MenuItem>
                <MenuItem value="UNAVAILABLE">Unavailable</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select
                value={categoryFilter}
                label="Category"
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <MenuItem value="ALL">All Categories</MenuItem>
                <MenuItem value="PART">Part</MenuItem>
                <MenuItem value="TOOL">Tool</MenuItem>
                <MenuItem value="MATERIAL">Material</MenuItem>
                <MenuItem value="CONSUMABLE">Consumable</MenuItem>
                <MenuItem value="GSE">GSE</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Location</InputLabel>
              <Select
                value={locationFilter}
                label="Location"
                onChange={(e) => setLocationFilter(e.target.value)}
              >
                <MenuItem value="ALL">All Locations</MenuItem>
                {uniqueLocations.map((loc) => (
                  <MenuItem key={loc} value={loc}>
                    {loc}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Store Type</InputLabel>
              <Select
                value={storeTypeFilter}
                label="Store Type"
                onChange={(e) => setStoreTypeFilter(e.target.value)}
              >
                <MenuItem value="ALL">All Store Types</MenuItem>
                {STORE_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Autocomplete
              multiple
              size="small"
              disableCloseOnSelect
              options={aircraftFilterOptions}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={aircraftFilterOptions.filter((opt) => aircraftFilter.includes(opt.id))}
              onChange={(e, newValue) => setAircraftFilter(newValue.map((v) => v.id))}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Aircraft"
                  placeholder={aircraftFilter.length === 0 ? 'All Aircraft' : ''}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Condition</InputLabel>
              <Select
                value={conditionFilter}
                label="Condition"
                onChange={(e) => setConditionFilter(e.target.value)}
              >
                <MenuItem value="ALL">All conditions</MenuItem>
                {PART_UNIT_CONDITIONS.map((v) => (
                  <MenuItem key={v} value={v}>{PART_CONDITION_LABELS[v] || v}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {operators.length > 0 && (
            <Grid item xs={12} sm={4} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Operator (PDF)</InputLabel>
                <Select
                  value={selectedOperatorId}
                  label="Operator (PDF)"
                  onChange={(e) => {
                    const val = e.target.value;
                    setSelectedOperatorId(val);
                    if (val) localStorage.setItem(OPERATOR_STORAGE_KEY, val);
                    else localStorage.removeItem(OPERATOR_STORAGE_KEY);
                  }}
                >
                  <MenuItem value="">No letterpad</MenuItem>
                  {operators.map((op) => (
                    <MenuItem key={op._id} value={op._id}>
                      {op.name} ({op.code})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="inv-sort-field-label">Sort by</InputLabel>
              <Select
                labelId="inv-sort-field-label"
                label="Sort by"
                value={orderBy}
                onChange={(e) => {
                  const nextOrderBy = e.target.value;
                  setOrderBy(nextOrderBy);
                  setPage(0);
                  persistTablePrefs({ columnOrder, orderBy: nextOrderBy, order });
                }}
              >
                {INVENTORY_SORT_OPTIONS.map((opt) => (
                  <MenuItem key={opt.id} value={opt.id}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel id="inv-sort-dir-label">Direction</InputLabel>
              <Select
                labelId="inv-sort-dir-label"
                label="Direction"
                value={order}
                onChange={(e) => {
                  const nextOrder = e.target.value;
                  setOrder(nextOrder);
                  setPage(0);
                  persistTablePrefs({ columnOrder, orderBy, order: nextOrder });
                }}
              >
                <MenuItem value="asc">Ascending</MenuItem>
                <MenuItem value="desc">Descending</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4} md={1.5}>
            <Button
              fullWidth
              variant="outlined"
              size="small"
              startIcon={<ClearIcon />}
              onClick={handleClearFilters}
              disabled={!hasActiveFilters()}
            >
              Clear
            </Button>
          </Grid>
          <Grid item xs={12} sm={4} md={1.5}>
            {columnReorderMode ? (
              <Button
                fullWidth
                variant="contained"
                size="small"
                startIcon={<ViewColumn />}
                onClick={handleDoneColumnReorder}
              >
                Done
              </Button>
            ) : (
              <Tooltip title="Enable column drag-to-reorder. Changes are saved automatically.">
                <Button
                  fullWidth
                  variant="outlined"
                  size="small"
                  startIcon={<Edit />}
                  onClick={handleStartColumnReorder}
                >
                  Edit columns
                </Button>
              </Tooltip>
            )}
          </Grid>
          <Grid item xs={12} sm={4} md={1.5}>
            <Tooltip title="Restore the default column order">
              <span>
                <Button
                  fullWidth
                  variant="outlined"
                  size="small"
                  startIcon={<ViewColumn />}
                  onClick={handleResetColumnOrder}
                  disabled={!isCustomColumnOrder}
                >
                  Reset columns
                </Button>
              </span>
            </Tooltip>
          </Grid>
          <Grid item xs={12} sm={4} md={1.5}>
            <Button
              fullWidth
              variant={showArchived ? 'contained' : 'outlined'}
              size="small"
              startIcon={<Archive />}
              onClick={() => setShowArchived(!showArchived)}
            >
              {showArchived ? 'Hide Archived' : 'Show Archived'}
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleExportCsv}
            >
              Export CSV
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Button
              fullWidth
              variant="outlined"
              color="warning"
              startIcon={<Undo />}
              disabled={revertingLink || !lastLinkRunId}
              onClick={handleRevertPartLinkRun}
            >
              {revertingLink ? 'Reverting...' : 'Revert Link'}
            </Button>
          </Grid>
        </Grid>
        {lastLinkRunId && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Last link run: {lastLinkRunId}
          </Typography>
        )}
        {filteredInventory.length !== inventory.length && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Showing {filteredInventory.length} of {inventory.length} items
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Statuses: <strong>IN_INVENTORY</strong> = available in store; <strong>ISSUED</strong> = out on issuance; <strong>QUARANTINED</strong> = held for inspection; <strong>RETURNED</strong> = returned from issuance; <strong>UNAVAILABLE</strong> = archived/unusable. Change status via Edit.
        </Typography>
      </Paper>

      {categoryFilter === 'TOOL' && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Tools (inventory)
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            For tools, the date field below is used as <strong>calibration due</strong> (not shelf expiry). The store dashboard lists tools when calibration is due within one month or overdue.
          </Alert>
          <Autocomplete
            size="small"
            options={filteredInventory}
            value={selectedToolPanel}
            onChange={(_, value) => setSelectedToolPanel(value)}
            getOptionLabel={(opt) =>
              opt ? `${opt.partNumber || ''}${opt.description ? ` — ${opt.description}` : ''}` : ''
            }
            isOptionEqualToValue={(a, b) => Boolean(a && b && String(a._id) === String(b._id))}
            renderInput={(params) => (
              <TextField {...params} label="Select a tool to view details" placeholder="Search part number or description" />
            )}
          />
          {selectedToolPanel && (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Selected tool details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="caption" color="text.secondary">Part number</Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                    {selectedToolPanel.partNumber}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="caption" color="text.secondary">Status</Typography>
                  <Typography variant="body1"><Chip label={selectedToolPanel.status} size="small" /></Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="caption" color="text.secondary">Quantity</Typography>
                  <Typography variant="body1">{selectedToolPanel.quantity ?? 0}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Description</Typography>
                  <Typography variant="body1">{selectedToolPanel.description || '—'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="caption" color="text.secondary">Calibration due</Typography>
                  <Typography variant="body1">
                    {selectedToolPanel.expiryDate
                      ? new Date(selectedToolPanel.expiryDate).toLocaleDateString()
                      : '—'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="caption" color="text.secondary">Location</Typography>
                  <Typography variant="body1">
                    {getLocationString(selectedToolPanel) || '—'}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </Paper>
      )}

      {/* Inventory Table */}
      <TableContainer component={Paper}>
        {columnReorderMode && (
          <Alert
            severity="info"
            sx={{ borderRadius: 0 }}
            action={(
              <Button color="inherit" size="small" onClick={handleDoneColumnReorder}>
                Done
              </Button>
            )}
          >
            Column reorder mode — drag the grips on headers to rearrange. Order is saved automatically.
          </Alert>
        )}
        <Table>
          <TableHead>
            <TableRow>
              {columnOrder.map((columnId) => {
                const meta = INVENTORY_COLUMN_META[columnId] || { align: 'left' };
                const isDragging = columnReorderMode && dragColumnId === columnId;
                const isDropTarget = columnReorderMode
                  && dropTargetColumnId === columnId
                  && dragColumnId
                  && dragColumnId !== columnId;
                return (
                  <TableCell
                    key={columnId}
                    align={meta.align || 'left'}
                    sortDirection={orderBy === columnId ? order : false}
                    onDragOver={columnReorderMode ? (e) => handleColumnDragOver(e, columnId) : undefined}
                    onDrop={columnReorderMode ? (e) => handleColumnDrop(e, columnId) : undefined}
                    onDragLeave={columnReorderMode ? () => {
                      if (dropTargetColumnId === columnId) setDropTargetColumnId(null);
                    } : undefined}
                    sx={{
                      opacity: isDragging ? 0.45 : 1,
                      borderLeft: isDropTarget ? '2px solid' : undefined,
                      borderColor: isDropTarget ? 'primary.main' : undefined,
                      whiteSpace: 'nowrap',
                      userSelect: columnReorderMode ? 'none' : undefined
                    }}
                  >
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                      {columnReorderMode && (
                        <Tooltip title="Drag to reorder column">
                          <Box
                            component="span"
                            draggable
                            onDragStart={(e) => handleColumnDragStart(e, columnId)}
                            onDragEnd={handleColumnDragEnd}
                            onClick={(e) => e.stopPropagation()}
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              cursor: 'grab',
                              color: 'text.secondary',
                              '&:active': { cursor: 'grabbing' }
                            }}
                          >
                            <DragIndicator fontSize="small" />
                          </Box>
                        </Tooltip>
                      )}
                      <TableSortLabel
                        active={orderBy === columnId}
                        direction={orderBy === columnId ? order : 'asc'}
                        onClick={() => handleSortRequest(columnId)}
                      >
                        {getColumnHeaderLabel(columnId)}
                      </TableSortLabel>
                    </Box>
                  </TableCell>
                );
              })}
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredInventory.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columnOrder.length + 1} sx={{ textAlign: 'center', py: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    {hasActiveFilters() || showArchived
                      ? 'No items match the current filters'
                      : 'No inventory items found'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
                filteredInventory
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((item) => (
                <TableRow
                  key={item._id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => setDetailItemId(item._id)}
                >
                  {columnOrder.map((columnId) => (
                    <TableCell
                      key={columnId}
                      align={INVENTORY_COLUMN_META[columnId]?.align || 'left'}
                    >
                      {renderInventoryColumnCell(columnId, item)}
                    </TableCell>
                  ))}
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Attachments">
                        <IconButton size="small" onClick={() => handleDocumentsDialogOpen(item)}>
                          <AttachFile fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {canEdit && !item.isArchived && (
                        <>
                          {isInventoryShelfLifeExpiringSoon(item) && item.status !== 'QUARANTINED' && (
                            <>
                              <Tooltip title="Move to quarantine store (shelf life expiring)">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => setQuarantineDialog({ open: true, item })}
                                >
                                  <Block fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Formal store transfer (via issuance approval)">
                                <IconButton
                                  size="small"
                                  color="warning"
                                  onClick={() => navigate('/issuance', {
                                    state: { recordType: 'transfer', transferItemId: item._id }
                                  })}
                                >
                                  <SwapHoriz fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                          <Tooltip title="Edit Item">
                            <IconButton size="small" onClick={() => handleOpen(item)}>
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Archive Item">
                            <IconButton 
                              size="small" 
                              color="warning"
                              onClick={() => setArchiveDialog({ open: true, item, action: 'archive' })}
                            >
                              <Archive fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      {canEdit && item.isArchived && (
                        <>
                          <Tooltip title="Edit location">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDetailItemId(item._id);
                              }}
                            >
                              <Place fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Restore Item">
                            <IconButton 
                              size="small" 
                              color="primary"
                              onClick={() => setArchiveDialog({ open: true, item, action: 'restore' })}
                            >
                              <Restore fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Permanently Delete">
                            <IconButton 
                              size="small" 
                              color="error"
                              onClick={() => setDeleteDialog({ open: true, item, dependencies: null })}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filteredInventory.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Rows per page:"
          showFirstButton
          showLastButton
        />
      </TableContainer>

      <InventoryItemFormDialog
        open={open}
        onClose={handleClose}
        submitMode="save"
        item={editingItem}
        aircraftList={aircraftList}
        storeApi={storeApi}
        onSaved={handleInventorySaved}
        onDocumentsOpen={handleDocumentsDialogOpen}
      />

      <StoreDocumentsDialog
        open={documentsDialog.open}
        onClose={handleDocumentsDialogClose}
        entityId={documentsDialog.entityId}
        entityType="inventory"
        entityLabel={documentsDialog.label}
        readOnly={!canEdit}
      />

      <InventoryItemDetailModal
        itemId={detailItemId}
        inventoryDomain={inventoryDomain}
        open={!!detailItemId}
        onClose={() => setDetailItemId(null)}
        onItemUpdated={() => fetchInventory()}
        operatorId={selectedOperatorId}
      />

      {/* Archive/Restore Confirmation Dialog */}
      <Dialog open={archiveDialog.open} onClose={() => setArchiveDialog({ open: false, item: null, action: null })}>
        <DialogTitle>
          {archiveDialog.action === 'archive' ? 'Archive Inventory Item' : 'Restore Inventory Item'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {archiveDialog.action === 'archive'
              ? `Are you sure you want to archive "${archiveDialog.item?.partNumber}"? The item will be hidden from the main inventory list but can be restored later.`
              : `Are you sure you want to restore "${archiveDialog.item?.partNumber}"? The item will be available in the inventory again.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setArchiveDialog({ open: false, item: null, action: null })}>Cancel</Button>
          <Button 
            onClick={archiveDialog.action === 'archive' ? handleArchive : handleRestore} 
            variant="contained"
            color={archiveDialog.action === 'archive' ? 'warning' : 'primary'}
          >
            {archiveDialog.action === 'archive' ? 'Archive' : 'Restore'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Quarantine Confirmation Dialog */}
      <Dialog open={quarantineDialog.open} onClose={() => setQuarantineDialog({ open: false, item: null })}>
        <DialogTitle>Move to Quarantine Store</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {isInventoryShelfLifeExpired(quarantineDialog.item)
              ? 'This item\u2019s shelf life has expired.'
              : 'This item\u2019s shelf life expires within 30 days.'}
          </Alert>
          <Typography>
            Move "{quarantineDialog.item?.partNumber}" to the quarantine store? Its store type will be set to
            {' '}<strong>Quarantine</strong> and its status to <strong>QUARANTINED</strong> (held for inspection).
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQuarantineDialog({ open: false, item: null })} disabled={quarantining}>Cancel</Button>
          <Button onClick={handleQuarantine} variant="contained" color="error" disabled={quarantining}>
            {quarantining ? 'Moving…' : 'Move to Quarantine'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, item: null, dependencies: null })} maxWidth="md" fullWidth>
        <DialogTitle>Permanently Delete Inventory Item</DialogTitle>
        <DialogContent>
          {deleteDialog.dependencies && deleteDialog.dependencies.hasDependencies ? (
            <Box>
              <Alert severity="error" sx={{ mb: 2 }}>
                Cannot delete this item because it has active dependencies. Please resolve these dependencies first.
              </Alert>
              
              {deleteDialog.dependencies.issuanceRecords && deleteDialog.dependencies.issuanceRecords.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Active Issuance Records ({deleteDialog.dependencies.issuanceRecords.length}):
                  </Typography>
                  {deleteDialog.dependencies.issuanceRecords.map((iss, idx) => (
                    <Typography key={idx} variant="body2" sx={{ ml: 2 }}>
                      • Issuance #{iss.id.slice(-8)} - Issued to: {iss.issuedTo} - Work Order: {iss.workOrderNumber} - Status: {iss.status}
                    </Typography>
                  ))}
                </Box>
              )}
              
              {deleteDialog.dependencies.reconciliationRecords && deleteDialog.dependencies.reconciliationRecords.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Reconciliation Records ({deleteDialog.dependencies.reconciliationRecords.length}):
                  </Typography>
                  {deleteDialog.dependencies.reconciliationRecords.map((rec, idx) => (
                    <Typography key={idx} variant="body2" sx={{ ml: 2 }}>
                      • Reconciliation #{rec.id.slice(-8)} - Date: {new Date(rec.reconciliationDate).toLocaleDateString()} - Type: {rec.type} - Status: {rec.status}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          ) : (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                This action cannot be undone. The item "{deleteDialog.item?.partNumber}" will be permanently deleted from the system.
              </Alert>
              <Typography>
                Are you absolutely sure you want to permanently delete this inventory item?
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, item: null, dependencies: null })}>Cancel</Button>
          {!deleteDialog.dependencies?.hasDependencies && (
            <Button onClick={handleDelete} variant="contained" color="error">
              Delete Permanently
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InventoryManagement;

